/**
 * Cruce Contable — Auditoría de Contabilizaciones
 * Detecta documentos sin comprobante automático y borradores pendientes.
 * Permite contabilizar manualmente (individual o en lote).
 */
import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import DatePicker from '@/components/DatePicker'
import { useTenantLocale } from '@/lib/locale'
import { fmtDate } from '@/lib/utils'
import { ConfirmModal, useConfirmModal } from '@/components/ConfirmModal'
import apiClient from '@/lib/apiClient'
import { AlertTriangle, CheckCircle2, ChevronDown, ChevronRight, FileText, CreditCard, Truck, Banknote, Landmark, Loader2, Pencil, CheckCircle, XCircle, Trash2 } from 'lucide-react'
import { DataTable } from '@/components/ds/DataTable'
import {
  useAccountingAudit,
  useContabilizar,
  useContabilizarBatch,
  useVoucher,
  useApproveVoucher,
  useRejectVoucher,
  useDeleteVoucher,
  type AuditMissingDoc,
  type AuditDraftVoucher,
  type ContabilizarRequest,
  type AccountingVoucher,
} from '../hooks/useAccounting'
import { VoucherForm, AttachmentsSection, TYPE_LABELS, fmtCLP } from './VoucherForm'
import { StatusBadge } from '../../../components/ds/StatusBadge'
import { VOUCHER_STATUS } from '../../../components/ds/statusCatalogs'
import { Modal } from '@/components/ds/Modal'

const SOURCE_TYPE_MAP: Record<string, string> = {
  sale_documents: 'sale_document',
  customer_payments: 'customer_payment',
  supplier_invoices: 'supplier_invoice',
  supplier_payments: 'supplier_payment',
  factoring_operations: 'factoring_operation',
}

const SECTION_CONFIG = [
  { key: 'sale_documents' as const, label: 'Documentos de Venta', icon: FileText },
  { key: 'customer_payments' as const, label: 'Pagos de Clientes', icon: CreditCard },
  { key: 'supplier_invoices' as const, label: 'Facturas de Proveedor', icon: Truck },
  { key: 'supplier_payments' as const, label: 'Pagos a Proveedores', icon: Banknote },
  { key: 'factoring_operations' as const, label: 'Operaciones de Factoring', icon: Landmark },
]

function KpiCard({ label, value, variant }: {
  label: string
  value: number
  variant: 'danger' | 'warning' | 'neutral'
}) {
  const colors = {
    danger: value > 0
      ? 'bg-danger-surface border-danger-border text-danger-content'
      : 'bg-success-surface border-success-border text-success-content',
    warning: value > 0
      ? 'bg-warning-surface border-warning-border text-warning-content'
      : 'bg-success-surface border-success-border text-success-content',
    neutral: 'bg-surface-muted border-divider text-content',
  }
  return (
    <div className={`rounded-xl border p-4 ${colors[variant]}`}>
      <p className="text-sm font-medium opacity-80">{label}</p>
      <p className="text-2xl font-bold mt-1">{value}</p>
    </div>
  )
}

function MissingSection({ label, icon: Icon, docs, sectionKey, onContabilizar, loadingId }: {
  label: string
  icon: React.ElementType
  docs: AuditMissingDoc[]
  sectionKey: string
  onContabilizar: (sourceType: string, sourceId: string) => void
  loadingId: string | null
}) {
  const locale = useTenantLocale()
  const [open, setOpen] = useState(docs.length > 0 && docs.length <= 5)
  if (docs.length === 0) return null

  const sourceType = SOURCE_TYPE_MAP[sectionKey]

  return (
    <div className="border border-divider rounded-lg overflow-hidden">
      <button
        onClick={() => setOpen(p => !p)}
        className="w-full flex items-center gap-3 px-4 py-3 bg-surface-muted hover:bg-soft transition-colors"
      >
        {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        <Icon size={16} className="text-content-muted" />
        <span className="text-sm font-medium text-content flex-1 text-left">{label}</span>
        <span className="text-xs font-semibold bg-danger-surface-strong text-danger-content px-2 py-0.5 rounded-full">
          {docs.length}
        </span>
      </button>
      {open && (
        <DataTable
          columns={[
            { key: 'type', header: 'Tipo' },
            { key: 'label', header: 'Documento', render: doc => <span className="font-medium text-content">{doc.label}</span> },
            { key: 'date', header: 'Fecha', render: doc => fmtDate(doc.date, locale) },
            {
              key: 'total', header: 'Total', numeric: true,
              sortValue: doc => Number(doc.total),
              render: doc => <span className="font-mono text-content">{Number(doc.total).toLocaleString()}</span>,
            },
            {
              key: 'status', header: 'Estado',
              render: doc => <span className="text-xs bg-surface-muted text-content-muted px-2 py-0.5 rounded">{doc.status}</span>,
            },
            {
              key: 'accion', header: 'Acción',
              render: doc => (
                <div className="text-center">
                  <button
                    onClick={() => onContabilizar(sourceType, doc.id)}
                    disabled={loadingId === doc.id}
                    className="text-xs text-primary hover:text-primary-hover font-medium disabled:opacity-50"
                  >
                    {loadingId === doc.id ? <Loader2 size={14} className="inline animate-spin" /> : 'Contabilizar'}
                  </button>
                </div>
              ),
            },
          ]}
          rows={docs}
          rowKey={doc => doc.id}
          surface={false}
          minWidth={760}
          caption="Documentos sin contabilizar"
        />
      )}
    </div>
  )
}

function DraftVouchersTable({ vouchers, onRowClick }: {
  vouchers: AuditDraftVoucher[]
  onRowClick: (id: string) => void
}) {
  const locale = useTenantLocale()
  if (vouchers.length === 0) return null

  const sourceLabels: Record<string, string> = {
    sale: 'Venta',
    purchase: 'Compra',
    factoring: 'Factoring',
  }

  return (
    <div>
      <h3 className="text-sm font-semibold text-content mb-2">
        Comprobantes Borradores Pendientes
      </h3>
      <DataTable
        columns={[
          { key: 'number', header: '#', render: v => <span className="font-mono">{v.number || '-'}</span> },
          { key: 'voucher_type', header: 'Tipo' },
          {
            key: 'source_type', header: 'Origen',
            render: v => (
              <span className="text-xs bg-info-surface-strong text-info-content px-2 py-0.5 rounded">
                {sourceLabels[v.source_type] || v.source_type}
              </span>
            ),
          },
          { key: 'date', header: 'Fecha', render: v => fmtDate(v.date, locale) },
          {
            key: 'description', header: 'Descripción',
            render: v => <span className="block max-w-[200px] truncate">{v.description}</span>,
          },
          {
            key: 'total_debe', header: 'Debe', numeric: true,
            sortValue: v => Number(v.total_debe),
            render: v => <span className="font-mono">{Number(v.total_debe).toLocaleString()}</span>,
          },
          {
            key: 'total_haber', header: 'Haber', numeric: true,
            sortValue: v => Number(v.total_haber),
            render: v => <span className="font-mono">{Number(v.total_haber).toLocaleString()}</span>,
          },
          {
            key: 'is_balanced', header: 'Cuadra',
            render: v => v.is_balanced
              ? <CheckCircle2 size={16} className="text-success-solid" />
              : <AlertTriangle size={16} className="text-warning-solid" />,
          },
        ]}
        rows={vouchers}
        rowKey={v => v.id}
        onRowClick={v => onRowClick(v.id)}
        minWidth={980}
        caption="Comprobantes en borrador pendientes"
      />
    </div>
  )
}


/**
 * Modal de detalle del comprobante mostrado IN-PLACE desde el Cruce Contable, para
 * revisar y actuar (aprobar/rechazar/eliminar/editar) sin navegar a /accounting/vouchers.
 * Reusa los building blocks del módulo (useVoucher, AttachmentsSection, VoucherForm).
 */
function AuditVoucherDetailModal({ voucherId, onClose, onEdit, onChanged }: {
  voucherId: string
  onClose: () => void
  onEdit: (v: AccountingVoucher) => void
  onChanged: () => void
}) {
  const locale = useTenantLocale()
  const { data: voucher, isLoading } = useVoucher(voucherId)
  const approve = useApproveVoucher()
  const reject = useRejectVoucher()
  const del = useDeleteVoucher()
  const { confirm, modalProps } = useConfirmModal()
  const [pdfLoading, setPdfLoading] = useState(false)
  const [err, setErr] = useState<string | null>(null)


  const done = () => { onChanged(); onClose() }
  const onErr = (e: unknown) => {
    const x = e as { response?: { data?: { error?: { message?: string } } } }
    setErr(x.response?.data?.error?.message ?? 'Ocurrió un error')
  }
  const handleApprove = () => {
    if (!voucher) return
    setErr(null); approve.mutate(voucher.id, { onSuccess: done, onError: onErr })
  }
  const handleReject = () => {
    if (!voucher) return
    const reason = prompt('Razón del rechazo (opcional):') ?? ''
    setErr(null); reject.mutate({ id: voucher.id, reason }, { onSuccess: done, onError: onErr })
  }
  const handleDelete = () => {
    if (!voucher) return
    confirm({
      message: '¿Eliminar este comprobante?', variant: 'danger',
      onConfirm: () => del.mutate(voucher.id, { onSuccess: done, onError: onErr }),
    })
  }
  const handleDownloadPdf = async () => {
    if (!voucher || pdfLoading) return
    setPdfLoading(true)
    try {
      const res = await apiClient.get(`/accounting/vouchers/${voucher.id}/pdf/`, { responseType: 'blob' })
      window.open(URL.createObjectURL(res.data), '_blank')
    } catch { /* noop */ } finally { setPdfLoading(false) }
  }

  return (
    <>
      <Modal
        open
        onClose={onClose}
        size="lg"
        title={
        <span>
          Detalle del Comprobante
          {voucher?.number && <span className="ml-2 text-content-subtle font-mono text-sm">#{voucher.number}</span>}
        </span>
      }
        actions={
          <>
            {voucher && (
              <div className="flex flex-1 flex-wrap items-center justify-between gap-2">
                <div className="flex gap-2">
                  {voucher.status === 'draft' && (
                    <>
                      <button onClick={() => onEdit(voucher)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-content-muted border border-divider rounded-lg hover:bg-soft">
                        <Pencil size={13} />Editar
                      </button>
                      <button onClick={handleApprove} disabled={approve.isPending}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-success-content border border-success-border rounded-lg hover:bg-success-surface disabled:opacity-60">
                        {approve.isPending ? <Loader2 size={13} className="animate-spin" /> : <CheckCircle size={13} />}Aprobar
                      </button>
                      <button onClick={handleReject}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-orange-600 dark:text-orange-300 border border-orange-200 dark:border-orange-800 rounded-lg hover:bg-orange-50 dark:hover:bg-orange-900/30">
                        <XCircle size={13} />Rechazar
                      </button>
                      <button onClick={handleDelete}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-danger-content-soft border border-danger-border rounded-lg hover:bg-danger-surface">
                        <Trash2 size={13} />Eliminar
                      </button>
                    </>
                  )}
                </div>
                <div className="flex gap-2">
                  <button onClick={handleDownloadPdf} disabled={pdfLoading}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-primary-hover bg-primary-50 border border-primary-200 rounded-lg hover:bg-primary-100 disabled:opacity-60">
                    {pdfLoading ? <Loader2 size={13} className="animate-spin" /> : <FileText size={13} />}Descargar PDF
                  </button>
                  <button onClick={onClose}
                    className="px-4 py-1.5 text-sm text-content-muted border border-divider rounded-lg hover:bg-soft">Cerrar</button>
                </div>
              </div>
            )}
          </>
        }
      >
        {isLoading ? (
          <div className="flex items-center justify-center py-16 text-content-subtle">
            <Loader2 size={20} className="animate-spin mr-2" />Cargando...
          </div>
        ) : voucher ? (
          <>
            <div className="space-y-4">
              {err && (
                <div className="bg-danger-surface border border-danger-border rounded-lg px-3 py-2 text-sm text-danger-content">{err}</div>
              )}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div><p className="text-xs text-content-muted mb-0.5">Tipo</p><p className="font-medium">{TYPE_LABELS[voucher.voucher_type]}</p></div>
                <div><p className="text-xs text-content-muted mb-0.5">Fecha</p><p className="font-medium">{fmtDate(voucher.date, locale)}</p></div>
                <div className="col-span-2"><p className="text-xs text-content-muted mb-0.5">Descripción</p><p className="font-medium">{voucher.description}</p></div>
                <div>
                  <p className="text-xs text-content-muted mb-0.5">Estado</p>
                  <StatusBadge status={voucher.status} catalog={VOUCHER_STATUS} />
                </div>
              </div>
              <div>
                <p className="text-xs font-medium text-content-muted uppercase mb-2">Líneas del comprobante</p>
                <DataTable
                  columns={[
                    {
                      key: 'account', header: 'Cuenta',
                      render: line => <span className="font-mono text-xs text-content">{line.account_code} — {line.account_name}</span>,
                    },
                    { key: 'cost_center_code', header: 'CC', render: line => <span className="text-xs text-info-content-soft">{line.cost_center_code || '—'}</span> },
                    { key: 'glosa', header: 'Glosa', render: line => line.glosa || '—' },
                    { key: 'debe', header: 'Debe', numeric: true, render: line => fmtCLP(line.debe) },
                    { key: 'haber', header: 'Haber', numeric: true, render: line => fmtCLP(line.haber) },
                  ]}
                  rows={voucher.lines}
                  rowKey={(line, i) => line.id ?? i}
                  footer={{ debe: fmtCLP(voucher.total_debe), haber: fmtCLP(voucher.total_haber) }}
                  surface={false}
                  minWidth={640}
                  caption="Líneas del comprobante"
                />
              </div>
              <AttachmentsSection voucherId={voucher.id} />
            </div>
          </>
        ) : null}
      </Modal>
      <ConfirmModal {...modalProps} />
    </>
  )
}

export function AccountingAuditTab() {
  const qc = useQueryClient()
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [loadingId, setLoadingId] = useState<string | null>(null)
  const [batchResult, setBatchResult] = useState<{ success: number; errors: number } | null>(null)
  const [detailId, setDetailId] = useState<string | null>(null)
  const [editingVoucher, setEditingVoucher] = useState<AccountingVoucher | null>(null)
  const [formOpen, setFormOpen] = useState(false)
  const refreshAudit = () => qc.invalidateQueries({ queryKey: ['accounting', 'audit'] })

  const params = {
    ...(dateFrom ? { date_from: dateFrom } : {}),
    ...(dateTo ? { date_to: dateTo } : {}),
  }
  const { data, isLoading } = useAccountingAudit(
    Object.keys(params).length > 0 ? params : undefined
  )

  const contabilizar = useContabilizar()
  const contabilizarBatch = useContabilizarBatch()

  const kpis = data?.kpis
  const totalIssues = (kpis?.total_missing_vouchers ?? 0) + (kpis?.total_draft_vouchers ?? 0)
  const totalMissing = kpis?.total_missing_vouchers ?? 0

  const handleContabilizarSingle = (sourceType: string, sourceId: string) => {
    setLoadingId(sourceId)
    setBatchResult(null)
    contabilizar.mutate({ source_type: sourceType, source_id: sourceId }, {
      onSettled: () => setLoadingId(null),
    })
  }

  const handleContabilizarAll = () => {
    setBatchResult(null)
    // Collect all missing items
    const items: ContabilizarRequest[] = []
    if (data?.missing) {
      for (const sec of SECTION_CONFIG) {
        const sourceType = SOURCE_TYPE_MAP[sec.key]
        for (const doc of data.missing[sec.key]) {
          items.push({ source_type: sourceType, source_id: doc.id })
        }
      }
    }
    contabilizarBatch.mutate(items.length > 0 ? items : undefined, {
      onSuccess: (result) => setBatchResult({ success: result.success, errors: result.errors }),
    })
  }

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6">
      {/* Acción de contabilización masiva. El título/subtítulo de la página los
          renderiza AccountingPage (PageHeader); no se repiten aquí. */}
      {totalMissing > 0 && (
        <div className="flex items-center justify-end">
          <button
            onClick={handleContabilizarAll}
            disabled={contabilizarBatch.isPending}
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-on-primary text-sm font-medium rounded-lg hover:bg-primary-hover disabled:opacity-60"
          >
            {contabilizarBatch.isPending && <Loader2 size={16} className="animate-spin" />}
            Contabilizar Todos ({totalMissing})
          </button>
        </div>
      )}

      {/* Batch result feedback */}
      {batchResult && (
        <div className={`p-3 rounded-lg text-sm ${
          batchResult.errors === 0
            ? 'bg-success-surface border border-success-border text-success-content'
            : 'bg-warning-surface border border-warning-border text-warning-content'
        }`}>
          {batchResult.success} contabilizados correctamente.
          {batchResult.errors > 0 && ` ${batchResult.errors} con errores.`}
        </div>
      )}

      {/* Date filters */}
      <div className="flex items-center gap-4">
        <div>
          <label htmlFor="accountingaudittab-fecha-desde" className="block text-xs text-content-muted mb-1">Fecha desde</label>
          <DatePicker id="accountingaudittab-fecha-desde" value={dateFrom} onChange={setDateFrom} />
        </div>
        <div>
          <label htmlFor="accountingaudittab-fecha-hasta" className="block text-xs text-content-muted mb-1">Fecha hasta</label>
          <DatePicker id="accountingaudittab-fecha-hasta" value={dateTo} onChange={setDateTo} />
        </div>
        {(dateFrom || dateTo) && (
          <button
            onClick={() => { setDateFrom(''); setDateTo('') }}
            className="text-xs text-primary hover:underline mt-4"
          >
            Limpiar
          </button>
        )}
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      ) : data ? (
        <>
          {/* KPI Cards */}
          <div className="grid grid-cols-3 gap-4">
            <KpiCard
              label="Sin Comprobante"
              value={kpis!.total_missing_vouchers}
              variant="danger"
            />
            <KpiCard
              label="Borradores Pendientes"
              value={kpis!.total_draft_vouchers}
              variant="warning"
            />
            <KpiCard
              label="Total Pendientes"
              value={totalIssues}
              variant="neutral"
            />
          </div>

          {/* Missing sections */}
          {kpis!.total_missing_vouchers > 0 && (
            <div className="space-y-2">
              {SECTION_CONFIG.map(sec => (
                <MissingSection
                  key={sec.key}
                  label={sec.label}
                  icon={sec.icon}
                  docs={data.missing[sec.key]}
                  sectionKey={sec.key}
                  onContabilizar={handleContabilizarSingle}
                  loadingId={loadingId}
                />
              ))}
            </div>
          )}

          {/* Draft vouchers */}
          <DraftVouchersTable vouchers={data.draft_vouchers} onRowClick={setDetailId} />

          {/* All OK */}
          {totalIssues === 0 && (
            <div className="flex items-center gap-3 p-4 bg-success-surface border border-success-border rounded-xl">
              <CheckCircle2 size={20} className="text-success-content-soft" />
              <p className="text-sm font-medium text-success-content">
                Todos los documentos estan contabilizados
              </p>
            </div>
          )}
        </>
      ) : null}

      {detailId && (
        <AuditVoucherDetailModal
          voucherId={detailId}
          onClose={() => setDetailId(null)}
          onEdit={v => { setDetailId(null); setEditingVoucher(v); setFormOpen(true) }}
          onChanged={refreshAudit}
        />
      )}
      {formOpen && (
        <VoucherForm
          voucher={editingVoucher}
          onClose={() => { setFormOpen(false); setEditingVoucher(null); refreshAudit() }}
        />
      )}
    </div>
  )
}
